/* eslint-disable */
import { useState, useEffect } from "react";
import {
  MsalProvider,
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, loginRequest, APP_ROLES } from "./authConfig";

// ─── MSAL instance (singleton) ───────────────────────────────────────────────
export const msalInstance = new PublicClientApplication(msalConfig);

// ─── Role extraction from ID token claims ────────────────────────────────────
export function getRolesFromAccount(account) {
  if (!account) return [];
  return account.idTokenClaims?.roles || [];
}

export function hasRole(account, role) {
  return getRolesFromAccount(account).includes(role);
}

export function isAuthorized(account) {
  const roles = getRolesFromAccount(account);
  return roles.includes(APP_ROLES.ADMIN) || roles.includes(APP_ROLES.ADVISOR);
}

// ─── Login screen ─────────────────────────────────────────────────────────────
const C = {
  navy: "#1e3a5f", blue: "#3b82f6", text: "#0f172a",
  textSec: "#64748b", textMuted: "#94a3b8", surface: "#f8fafc",
  border: "#e8ecf1",
};

function LoginScreen({ onLogin, error }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: "48px 40px", maxWidth: 400,
        width: "90%", boxShadow: "0 24px 64px rgba(0,0,0,.18)", textAlign: "center",
      }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: C.navy, letterSpacing: "-0.03em" }}>
            204Plans
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>
            Harness Financial, LLC
          </div>
          <div style={{ width: 48, height: 3, background: `linear-gradient(90deg, ${C.navy}, ${C.blue})`, borderRadius: 2, margin: "16px auto 0" }} />
        </div>

        <div style={{ fontSize: 14, color: C.textSec, marginBottom: 32, lineHeight: 1.6 }}>
          Section 204 RMD Planning Tool
        </div>

        {error && (
          <div style={{ background: "#fff0f3", border: "1px solid #fecdd3", borderRadius: 10, padding: "12px 16px", marginBottom: 24, fontSize: 13, color: "#be123c", textAlign: "left" }}>
            <strong>Access denied.</strong> Your account does not have the Admin or Advisor role
            required for this application. Contact your administrator.
          </div>
        )}

        <button
          onClick={onLogin}
          style={{
            width: "100%", padding: "14px 24px", borderRadius: 12, border: "none",
            background: `linear-gradient(135deg, ${C.navy} 0%, #2d5a87 100%)`,
            color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 10, boxShadow: "0 4px 14px rgba(30,58,95,.3)",
          }}
        >
          {/* Microsoft logo mark */}
          <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Sign in with Microsoft
        </button>

        <div style={{ marginTop: 24, fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>
          Uses your existing H2 Fin Portal credentials.<br />
          No separate account required.
        </div>
      </div>
    </div>
  );
}

// ─── Auth gate — wraps the app, enforces role check ──────────────────────────
function AuthGate({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [authError, setAuthError] = useState(false);
  const [silentAttempted, setSilentAttempted] = useState(false);

  // Attempt silent SSO on mount — picks up existing portal session automatically
  useEffect(() => {
    if (!isAuthenticated && !silentAttempted) {
      setSilentAttempted(true);
      instance.ssoSilent(loginRequest).catch(() => {
        // No active session — user will need to click sign in
      });
    }
  }, [instance, isAuthenticated, silentAttempted]);

  // Set active account once authenticated
  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const handleLogin = () => {
    setAuthError(false);
    instance.loginRedirect(loginRequest);
  };

  // Check role on the active account
  const account = instance.getActiveAccount() || accounts[0];
  if (isAuthenticated && account && !isAuthorized(account)) {
    return <LoginScreen onLogin={handleLogin} error={true} />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        {children}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen onLogin={handleLogin} error={authError} />
      </UnauthenticatedTemplate>
    </>
  );
}

// ─── Public export — wrap your App with this ─────────────────────────────────
export function AuthWrapper({ children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>
        {children}
      </AuthGate>
    </MsalProvider>
  );
}

// ─── Hook for components that need user info ─────────────────────────────────
export function useCurrentUser() {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() || accounts[0] || null;
  if (!account) return { name: null, email: null, roles: [], isAdmin: false };
  const roles = getRolesFromAccount(account);
  return {
    name:    account.name || account.username,
    email:   account.username,
    roles,
    isAdmin: roles.includes(APP_ROLES.ADMIN),
  };
}
